import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const MAX_FRIEND_SLOTS = 3;

export type FriendInviteRow = {
  id: string;
  event_id: string;
  status: "reserved" | "claimed" | "expired" | "revoked";
  friend_first_name: string | null;
  slot_index: number;
  expires_at: string;
  claimed_at: string | null;
  source_ticket_id: string | null;
  created_at: string;
};

export type BringFriendEligibility = {
  eligible: boolean;
  source: string;
  campaign_active: boolean;
  reason?: string;
  campaign_starts_at?: string | null;
  campaign_ends_at?: string | null;
  max_seats?: number;
};

/** Invites that count against the allowance for the current campaign window. */
export const activeInvitesInWindow = (
  invites: FriendInviteRow[] | undefined,
  eligibility?: BringFriendEligibility,
) =>
  (invites ?? []).filter((invite) => {
    if (!isActiveInvite(invite)) return false;
    const created = new Date(invite.created_at).getTime();
    const start = eligibility?.campaign_starts_at
      ? new Date(eligibility.campaign_starts_at).getTime()
      : null;
    const end = eligibility?.campaign_ends_at
      ? new Date(eligibility.campaign_ends_at).getTime()
      : null;
    if (start !== null && created < start) return false;
    if (end !== null && created > end) return false;
    return true;
  });

export const isActiveInvite = (invite: FriendInviteRow) =>
  invite.status === "reserved" || invite.status === "claimed";

export function useBringFriendEligibility(eventId: string | undefined) {
  return useQuery({
    queryKey: ["bring-friend-eligibility", eventId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "get_event_bring_friend_eligibility",
        { _event_id: eventId! },
      );
      if (error) throw error;
      return data as unknown as BringFriendEligibility;
    },
    enabled: !!eventId,
  });
}

/**
 * All invites created by the signed-in user, campaign-wide.
 * The 3-seat allowance is campaign-wide, so slot state can't be scoped to one event.
 */
export function useMyFriendInvites(enabled = true) {
  return useQuery<FriendInviteRow[]>({
    queryKey: ["friend-invites", "mine"],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("friend_invites")
        .select(
          "id, event_id, status, friend_first_name, slot_index, expires_at, claimed_at, source_ticket_id, created_at",
        )
        .eq("inviter_id", user.id)
        .order("slot_index", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as FriendInviteRow[];
    },
    enabled,
  });
}

/** Remaining friend seats for the signed-in user, scoped to the current campaign. */
export function useFriendSeatsRemaining(enabled = true, eventId?: string) {
  const query = useMyFriendInvites(enabled);
  const { data: eligibility } = useBringFriendEligibility(
    enabled ? eventId : undefined,
  );
  const max = eligibility?.max_seats ?? MAX_FRIEND_SLOTS;
  const used = activeInvitesInWindow(query.data, eligibility).length;
  return { ...query, used, max, remaining: Math.max(0, max - used) };
}

const invalidateInvites = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ["friend-invites", "mine"] });

export function useReserveInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      eventId: string;
      sourceTicketId: string;
      friendFullName?: string;
    }) => {
      const { data, error } = await supabase.rpc("reserve_bring_friend_invite", {
        _event_id: params.eventId,
        _source_ticket_id: params.sourceTicketId,
        _friend_first_name: params.friendFullName?.trim() || null,
        _mode: "share",
      });
      if (error) throw error;
      return data as unknown as {
        success: boolean;
        invite_id: string;
        token: string;
      };
    },
    onSuccess: () => invalidateInvites(qc),
  });
}

/** Update the friend's full name on a reserved (not yet claimed) invite. */
export function useUpdateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { inviteId: string; friendFullName: string }) => {
      const { data, error } = await supabase.rpc("update_bring_friend_invite", {
        _invite_id: params.inviteId,
        _friend_first_name: params.friendFullName.trim(),
      });
      if (error) throw error;
      return data as unknown as { success: boolean };
    },
    onSuccess: () => invalidateInvites(qc),
  });
}

export function useRemintToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data, error } = await supabase.rpc(
        "remint_bring_friend_invite_token",
        { _invite_id: inviteId },
      );
      if (error) throw error;
      return data as unknown as { success: boolean; token: string };
    },
    onSuccess: () => invalidateInvites(qc),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data, error } = await supabase.rpc("revoke_bring_friend_invite", {
        _invite_id: inviteId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateInvites(qc),
  });
}

export function useClaimInvite() {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await supabase.rpc("claim_bring_friend_invite", {
        _token: token,
      });
      if (error) throw error;
      return data as unknown as {
        success: boolean;
        ticket_id: string;
        event_id: string;
      };
    },
  });
}

export function useInvitePreview(token: string | undefined) {
  return useQuery({
    queryKey: ["friend-invite-preview", token],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "get_bring_friend_invite_preview",
        { _token: token! },
      );
      if (error) throw error;
      return data as unknown as {
        valid: boolean;
        reason?: string;
        event_id?: string;
        event_title?: string;
        event_start_time?: string;
        inviter_first_name?: string;
        friend_first_name?: string;
      };
    },
    enabled: !!token,
  });
}
